/**
 * MindMeld Status Context
 * Adapted from HappyHippo's Status.tsx pattern
 * Simplified for MindMeld's subscription-based model
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authApi } from '../api/auth';
import { userApi } from '../api/user';
import type { User, Client, UserEntitlement, UsageStats } from '../types';

interface StatusState {
  authStatus: boolean;
  isLoading: boolean;
  error: string | null;
  user: User | null;
  client: Client | null;
  entitlements: UserEntitlement[];
  usageStats: UsageStats | null;
}

interface StatusContextValue extends StatusState {
  authenticate: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  hasFeature: (feature: string) => boolean;
  canAddCollaborator: () => boolean;
  canCreateProject: () => boolean;
}

const StatusContext = createContext<StatusContextValue | null>(null);

export const useStatus = () => {
  const context = useContext(StatusContext);
  if (!context) {
    throw new Error('useStatus must be used within a StatusProvider');
  }
  return context;
};

export const StatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<StatusState>({
    authStatus: false,
    isLoading: true,
    error: null,
    user: null,
    client: null,
    entitlements: [],
    usageStats: null,
  });

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const storedEmail = localStorage.getItem('mindmeld_email');
      if (storedEmail) {
        try {
          await checkAuthStatus();
        } catch (error) {
          console.log('[Status] No valid session found');
          localStorage.removeItem('mindmeld_email');
          localStorage.removeItem('mindmeld_token');
        }
      }
      setState(prev => ({ ...prev, isLoading: false }));
    };

    checkSession();
  }, []);

  const loadUserData = async (email: string) => {
    console.log('[Status] Loading user data for:', email);

    // Fetch user profile
    const userResponse = await userApi.getUser(email);
    if (!userResponse.success || !userResponse.data) {
      throw new Error(userResponse.error || 'Failed to load user data');
    }

    // Extract user record from API response (handles Records[] wrapper)
    const rawData = userResponse.data as any;
    const records = rawData?.data?.Records || rawData?.Records || [];
    const record = records[0] || rawData;

    const user: User = {
      email: record.email_address || record.email || email,
      cognito_sub: record.cognito_sub || '',
      client_id: record.client_id || '',
      company_id: record.company_id || '',
      first_name: record.first_name || '',
      last_name: record.last_name || '',
      display_name: record.display_name || `${record.first_name || ''} ${record.last_name || ''}`.trim() || undefined,
      created_at: record.member_since || record.created_at || '',
    };

    // Build client from embedded subscription data (always present in userGet response)
    let client: Client | null = null;
    if (record.subscription) {
      client = {
        client_id: user.client_id,
        owner_email: user.email,
        subscription_tier: record.subscription.tier || 'free',
        subscription_status: record.subscription.status || 'active',
        stripe_customer_id: record.stripe_customer_id,
        created_at: user.created_at,
      } as Client;
    }

    // Build usage stats from embedded data (always present in userGet response)
    let usageStats: UsageStats | null = null;
    if (record.usage && record.limits) {
      usageStats = {
        collaborators: record.usage.collaborators || 0,
        projects: record.usage.projects || 0,
        invariants: record.usage.invariants || 0,
        maxCollaborators: record.limits.max_collaborators ?? null,
        maxProjects: record.limits.max_projects ?? null,
        maxInvariants: record.limits.max_invariants ?? null,
      };
    }

    // Fetch entitlements
    const entitlementsResponse = await userApi.getEntitlements(email);
    const entitlements = entitlementsResponse.success && entitlementsResponse.data ? entitlementsResponse.data : [];

    setState(prev => ({
      ...prev,
      user,
      client,
      entitlements,
      usageStats,
      error: null,
    }));

    return { user, client, entitlements, usageStats };
  };

  const authenticate = async (email: string, password: string) => {
    console.log('[Status] Authenticating...');
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const authResult = await authApi.signIn(email, password);

      if (!authResult.success) {
        throw new Error(authResult.error || 'Authentication failed');
      }

      // Store credentials
      localStorage.setItem('mindmeld_email', email);
      if (authResult.token) {
        localStorage.setItem('mindmeld_token', authResult.token);
      }

      // Load user data
      await loadUserData(email);

      setState(prev => ({
        ...prev,
        authStatus: true,
        isLoading: false,
      }));

      console.log('[Status] Authentication successful');
    } catch (error) {
      console.error('[Status] Authentication failed:', error);
      setState(prev => ({
        ...prev,
        authStatus: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Authentication failed',
        user: null,
        client: null,
        entitlements: [],
        usageStats: null,
      }));
      throw error;
    }
  };

  const logout = async () => {
    console.log('[Status] Logging out...');
    try {
      await authApi.signOut();
    } catch (error) {
      console.error('[Status] Logout error:', error);
    }

    localStorage.removeItem('mindmeld_email');
    localStorage.removeItem('mindmeld_token');

    setState({
      authStatus: false,
      isLoading: false,
      error: null,
      user: null,
      client: null,
      entitlements: [],
      usageStats: null,
    });
  };

  const checkAuthStatus = async () => {
    console.log('[Status] Checking auth status...');
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const session = await authApi.getCurrentSession();

      if (!session.valid) {
        throw new Error('Session invalid');
      }

      const email = session.email || localStorage.getItem('mindmeld_email');
      if (!email) {
        throw new Error('No email in session');
      }

      await loadUserData(email);

      setState(prev => ({
        ...prev,
        authStatus: true,
        isLoading: false,
      }));
    } catch (error) {
      console.error('[Status] Auth check failed:', error);
      setState(prev => ({
        ...prev,
        authStatus: false,
        isLoading: false,
        user: null,
        client: null,
        entitlements: [],
        usageStats: null,
      }));
      throw error;
    }
  };

  const refreshUserData = useCallback(async () => {
    const email = state.user?.email || localStorage.getItem('mindmeld_email');
    if (email) {
      await loadUserData(email);
    }
  }, [state.user?.email]);

  // Feature check based on subscription tier
  // Updated Tiering Model (2024-12):
  // - Free: NO standards (basic invariants only)
  // - Team: Community standards (curated set, no picker)
  // - Professional: Full library with standards picker
  // - Enterprise: Custom packs + full library + preferences
  const hasFeature = useCallback((feature: string): boolean => {
    const tierFeatures: Record<string, string[]> = {
      free: ['local_context', 'basic_invariants'], // NO standards
      team: [
        'local_context', 'shared_invariants', 'cross_platform_sync', 'team_memory',
        'community_standards' // Curated set, all enabled
      ],
      starter: [ // Legacy mapping -> team
        'local_context', 'shared_invariants', 'cross_platform_sync', 'team_memory',
        'community_standards'
      ],
      professional: [
        'local_context', 'shared_invariants', 'cross_platform_sync', 'team_memory',
        'priority_support', 'analytics', 'standards_picker',
        'community_standards', 'compliance_standards', 'aws_standards'
      ],
      enterprise: [
        'local_context', 'shared_invariants', 'cross_platform_sync', 'team_memory',
        'priority_support', 'analytics', 'standards_picker', 'custom_packs',
        'community_standards', 'compliance_standards', 'aws_standards',
        'sso', 'audit_trail', 'knowledge_curation',
        'attribution_tracking', 'dedicated_support', 'custom_integrations'
      ],
    };

    const tier = state.client?.subscription_tier || 'free';
    return tierFeatures[tier]?.includes(feature) || false;
  }, [state.client?.subscription_tier]);

  const canAddCollaborator = useCallback((): boolean => {
    if (!state.usageStats) return false;
    if (state.usageStats.maxCollaborators === null) return true;
    return state.usageStats.collaborators < state.usageStats.maxCollaborators;
  }, [state.usageStats]);

  const canCreateProject = useCallback((): boolean => {
    if (!state.usageStats) return false;
    if (state.usageStats.maxProjects === null) return true;
    return state.usageStats.projects < state.usageStats.maxProjects;
  }, [state.usageStats]);

  return (
    <StatusContext.Provider
      value={{
        ...state,
        authenticate,
        logout,
        checkAuthStatus,
        refreshUserData,
        hasFeature,
        canAddCollaborator,
        canCreateProject,
      }}
    >
      {children}
    </StatusContext.Provider>
  );
};

export default StatusContext;
